import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';

interface OfferState {
  id: string;
  accountManagerId: string;
  accountSaleId: string;
  companyId: string;
  enumOfferStatusId: number;
  number: string;
  date: string;
  dateStart: string;
  dateEnd: string;
  datePeriod: number;
  acquisitionType: string;
  isExpedite: boolean;
  intervention: number;
  hasUnlimitedInterventions: any;
  metadata: any;
  accountManager: any;
  accountSale: any;
  company: any;
  enumOfferStatus: any;
  offerWorkPlaceItems: any[];
  offerLaborExtras: any[];
  offerCommentsTab: any[];
  location: any[];
  equipmentTab: {
    [key: string]: any;
  };
  laborTab: {
    [key: string]: any;
  };
  props: {
    [key: string]: any;
  };
}

const currentDate = moment().format('YYYY-MM-DD');
const currentDateEnd = moment().add(1, 'year').format('YYYY-MM-DD');

const initialState: OfferState = {
  id: '',
  accountManagerId: '',
  accountSaleId: '',
  companyId: '',
  enumOfferStatusId: 0,
  number: '',
  date: currentDate,
  dateStart: currentDate,
  dateEnd: currentDateEnd,
  datePeriod: 30,
  acquisitionType: 'transfer bancar',
  isExpedite: false,
  intervention: 3,
  hasUnlimitedInterventions: false,
  metadata: null,
  accountManager: null,
  accountSale: null,
  company: null,
  enumOfferStatus: null,
  offerWorkPlaceItems: [],
  offerLaborExtras: [],
  offerCommentsTab: [],
  location: [],
  equipmentTab: { workPlaceId: null },
  laborTab: { laborPriceOnHour: 300 },
  props: { searchTerm: '', skip: 1, take: 10, totalItems: 0 },
};

const offerSlice = createSlice({
  name: 'offer',
  initialState,
  reducers: {
    setOffer: (state, action: PayloadAction<Partial<OfferState>>) => {
      return { ...state, ...action.payload };
    },
    updateField: (state: any, action: PayloadAction<{ field: any; value: any }>) => {
      const { field, value } = action.payload;
      state[field] = value;
    },
    updateEquipmentTab: (state, action: PayloadAction<{ field: string; value: any }>) => {
      const { field, value } = action.payload;
      // Dynamically update equipmentTab with the specified field and value
      state.equipmentTab = {
        ...state.equipmentTab,
        [field]: value,
      };
    },
    updateLaborTab: (state, action: PayloadAction<{ field: string; value: any }>) => {
      const { field, value } = action.payload;
      // Dynamically update equipmentTab with the specified field and value
      state.laborTab = {
        ...state.laborTab,
        [field]: value,
      };
    },
    addOfferWorkPlaceItem: (state, action) => {
      state.offerWorkPlaceItems.push(action.payload);
    },
    updateOfferWorkPlaceItem: (state, action: PayloadAction<{ index: number; updates: any }>) => {
      const { index, updates } = action.payload;
      if (state.offerWorkPlaceItems[index]) {
        state.offerWorkPlaceItems[index] = {
          ...state.offerWorkPlaceItems[index],
          ...updates,
        };
      }
    },
    removeOfferWorkPlaceItem: (state, action: PayloadAction<number>) => {
      const index = action.payload;
      if (index >= 0 && index < state.offerWorkPlaceItems.length) {
        state.offerWorkPlaceItems.splice(index, 1);
      }
    },
    addOfferLaborExtra: (state, action) => {
      state.offerLaborExtras.push(action.payload);
    },
    addOfferComment: (state, action: PayloadAction<any>) => {
      state.offerCommentsTab.push(action.payload);
    },
    updateOfferComment: (
      state,
      action: PayloadAction<{
        departmentName: string;
        updates: Partial<any>;
      }>
    ) => {
      const { departmentName, updates } = action.payload;
      const index = state.offerCommentsTab.findIndex((comment) => comment.departmentName === departmentName);

      if (index !== -1) {
        state.offerCommentsTab[index] = {
          ...state.offerCommentsTab[index],
          ...updates,
        };
      } else {
        state.offerCommentsTab.push({
          departmentName,
          ...updates,
        });
      }
    },
    // Remove a file from a comment
    removeOfferComment: (
      state,
      action: PayloadAction<{
        departmentName: string;
        fileUid: string;
      }>
    ) => {
      const { departmentName, fileUid } = action.payload;
      const commentIndex = state.offerCommentsTab.findIndex((comment) => comment.departmentName === departmentName);

      if (commentIndex !== -1) {
        const comment = state.offerCommentsTab[commentIndex];

        const updatedFiles = (comment.offerCommentFiles || []).filter(
          (file: any) => file.uid !== fileUid && file.id !== fileUid
        );

        state.offerCommentsTab[commentIndex] = {
          ...comment,
          offerCommentFiles: updatedFiles,
        };
      }
    },
    updateProps: (state, action: PayloadAction<{ field: string; value: any }>) => {
      const { field, value } = action.payload;
      state.props = {
        ...state.props,
        [field]: value,
      };
    },
  },
});

export const {
  setOffer,
  updateField,
  updateEquipmentTab, // Export the new action
  updateLaborTab,
  addOfferWorkPlaceItem,
  updateOfferWorkPlaceItem,
  removeOfferWorkPlaceItem,
  addOfferLaborExtra,
  addOfferComment,
  updateOfferComment,
  removeOfferComment,
  updateProps,
} = offerSlice.actions;

export default offerSlice.reducer;
